// Centralized, typed API endpoint functions for the TIPSSSO API (spec 4.8).
// Pages call these via TanStack Query. Domain shapes come from @tips-sso/shared
// where they exist; request/response wrappers specific to the admin API live here.
import type {
  AggregateQueryRequest,
  AggregateQueryResponse,
  Organization,
  OrgStatus,
  OrgType,
  ResellerCustomerPermissions,
  ResellerRelationship,
} from '@tips-sso/shared';
import { apiGet, apiPatch, apiPost } from './client.js';

// ---- Organizations (GET/POST /admin/orgs, GET/PATCH /admin/orgs/:orgId) ----

export interface OrgListFilters {
  orgType?: OrgType;
  status?: OrgStatus;
  limit?: number;
  offset?: number;
}

export function fetchOrgs(filters?: OrgListFilters): Promise<Organization[]> {
  return apiGet<Organization[]>('/admin/orgs', { params: filters });
}

// GET /admin/orgs/:orgId returns the org plus its entitlements + memberships,
// not a bare Organization — keep this shape in sync with routes/admin.ts.
export interface OrgDetail {
  org: Organization;
  entitlements: Record<string, unknown> | unknown[];
  memberships: unknown[];
}

export function fetchOrg(orgId: string): Promise<OrgDetail> {
  return apiGet<OrgDetail>(`/admin/orgs/${orgId}`);
}

export interface CreateOrgInput {
  legalName: string;
  displayName: string;
  orgType: OrgType;
  primaryDomain?: string;
  status?: OrgStatus;
}

export function createOrg(input: CreateOrgInput): Promise<Organization> {
  return apiPost<Organization>('/admin/orgs', input);
}

export interface UpdateOrgInput {
  displayName?: string;
  primaryDomain?: string | null;
  status?: OrgStatus;
}

export function updateOrg(orgId: string, input: UpdateOrgInput): Promise<Organization> {
  return apiPatch<Organization>(`/admin/orgs/${orgId}`, input);
}

// ---- Resellers (POST/GET /admin/resellers, .../:resellerOrgId/customers) ----

export function fetchResellers(): Promise<Organization[]> {
  return apiGet<Organization[]>('/admin/resellers');
}

export interface CreateResellerInput {
  legalName: string;
  displayName: string;
  primaryDomain?: string;
}

export function createReseller(input: CreateResellerInput): Promise<Organization> {
  return apiPost<Organization>('/admin/resellers', input);
}

/** A reseller customer pairs the relationship with the customer org's display fields. */
export interface ResellerCustomer extends ResellerRelationship {
  customerDisplayName?: string;
  customerPrimaryDomain?: string | null;
  customerStatus?: OrgStatus;
}

export function fetchResellerCustomers(resellerOrgId: string): Promise<ResellerCustomer[]> {
  return apiGet<ResellerCustomer[]>(`/admin/resellers/${resellerOrgId}/customers`);
}

export interface CreateResellerCustomerInput {
  legalName: string;
  displayName: string;
  primaryDomain?: string;
  markupPercent?: number;
  externalResellerCustomerId?: string;
}

export function createResellerCustomer(
  resellerOrgId: string,
  input: CreateResellerCustomerInput,
): Promise<ResellerCustomer> {
  return apiPost<ResellerCustomer>(`/admin/resellers/${resellerOrgId}/customers`, input);
}

// ---- Customer users + invitations -------------------------------------------

export interface OrgMembership {
  id: string;
  org_id: string;
  zitadel_user_id: string;
  email: string;
  membership_source: 'email_invite' | 'scim' | 'jit_sso' | 'manual_admin';
  status: 'active' | 'suspended' | 'deactivated';
  created_at: string;
  updated_at: string;
}

export function fetchCustomerUsers(customerOrgId: string): Promise<OrgMembership[]> {
  return apiGet<OrgMembership[]>(`/admin/customers/${customerOrgId}/users`);
}

export type InvitationType =
  | 'customer_user'
  | 'customer_admin'
  | 'reseller_user'
  | 'reseller_admin'
  | 'tips_internal_user';

export interface InviteUserInput {
  invitedEmail: string;
  invitationType: InvitationType;
  targetRoles: string[];
}

export interface UserInvitation {
  id: string;
  target_org_id: string;
  invited_email: string;
  invitation_type: InvitationType;
  target_roles: string[];
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at: string;
  created_at: string;
}

export function inviteCustomerUser(
  customerOrgId: string,
  input: InviteUserInput,
): Promise<UserInvitation> {
  return apiPost<UserInvitation>(`/admin/customers/${customerOrgId}/invitations`, input);
}

export interface UpdateUserInput {
  status?: 'active' | 'suspended' | 'deactivated';
  roles?: string[];
}

export function updateCustomerUser(
  customerOrgId: string,
  userId: string,
  input: UpdateUserInput,
): Promise<OrgMembership> {
  return apiPatch<OrgMembership>(`/admin/customers/${customerOrgId}/users/${userId}`, input);
}

// ---- Entitlements -----------------------------------------------------------

export interface Entitlement {
  productKey: string;
  entitlementKey: string;
  entitlementValue: string;
}

export function fetchEntitlements(customerOrgId: string): Promise<Entitlement[]> {
  return apiGet<Entitlement[]>(`/admin/customers/${customerOrgId}/entitlements`);
}

export interface GrantEntitlementInput {
  productKey: string;
  entitlementKey: string;
  entitlementValue: string;
  endsAt?: string | null;
}

export function grantEntitlement(
  customerOrgId: string,
  input: GrantEntitlementInput,
): Promise<Entitlement> {
  return apiPost<Entitlement>(`/admin/customers/${customerOrgId}/entitlements`, input);
}

// ---- Identity maturity profile ----------------------------------------------

export interface IdentityProfile {
  org_id: string;
  identity_maturity: 'invite_only' | 'external_idp' | 'scim';
  mfa_required: boolean;
  passkey_enabled: boolean;
  primary_login_brand_key: string | null;
  updated_at: string;
}

export function fetchIdentityProfile(customerOrgId: string): Promise<IdentityProfile> {
  return apiGet<IdentityProfile>(`/admin/customers/${customerOrgId}/identity`);
}

// ---- Microsoft consent + senders --------------------------------------------

export interface MicrosoftSender {
  id: string;
  org_id: string;
  sender_email: string;
  sender_display_name: string | null;
  sender_type: 'user_mailbox' | 'shared_mailbox' | 'service_mailbox';
  status: 'active' | 'disabled' | 'revoked' | 'verification_failed';
  created_at: string;
}

export interface MicrosoftConsentStatus {
  org_id: string;
  azure_tenant_id: string | null;
  tenant_display_name: string | null;
  consent_status: 'pending' | 'active' | 'revoked' | 'expired' | 'failed' | 'not_configured';
  consent_type: 'delegated_user' | 'application_admin_consent' | null;
  consented_by_email: string | null;
  consented_at: string | null;
}

/** Read-only consent status for a customer org (resellers may view, not approve). */
export function fetchMicrosoftConsent(customerOrgId: string): Promise<MicrosoftConsentStatus> {
  return apiGet<MicrosoftConsentStatus>('/microsoft/consent', { params: { orgId: customerOrgId } });
}

export function fetchMicrosoftSenders(customerOrgId: string): Promise<MicrosoftSender[]> {
  return apiGet<MicrosoftSender[]>('/microsoft/senders', { params: { orgId: customerOrgId } });
}

export interface CreateSenderInput {
  orgId: string;
  senderEmail: string;
  senderDisplayName?: string;
  senderType: 'user_mailbox' | 'shared_mailbox' | 'service_mailbox';
}

export function createMicrosoftSender(input: CreateSenderInput): Promise<MicrosoftSender> {
  return apiPost<MicrosoftSender>('/microsoft/senders', input);
}

export interface ConsentStartResult {
  consentUrl: string;
}

/** Customer-admin-only: begin the Graph admin-consent flow. */
export function startMicrosoftConsent(orgId: string): Promise<ConsentStartResult> {
  return apiPost<ConsentStartResult>('/microsoft/consent/start', { orgId });
}

// ---- SCIM connection + group→role mappings ----------------------------------

export interface ScimConnection {
  id: string;
  org_id: string;
  connection_type: 'zitadel_scim' | 'tips_scim_facade';
  idp_type: 'entra_id' | 'okta' | 'google_workspace' | 'onelogin' | 'other';
  status: string;
  scim_base_url: string | null;
  external_tenant_id: string | null;
  created_at: string;
  mappings?: ScimGroupRoleMapping[];
}

export interface ScimGroupRoleMapping {
  externalGroupName: string;
  externalGroupId?: string;
  targetRoleKey: string;
  targetProjectKey: string;
  assignmentScope: 'own_org' | 'downstream_customer_org' | 'reseller_managed_customer';
}

export interface ConfigureScimInput {
  connectionType: 'zitadel_scim' | 'tips_scim_facade';
  idpType: ScimConnection['idp_type'];
  scimBaseUrl?: string;
  externalTenantId?: string;
  groupRoleMappings: ScimGroupRoleMapping[];
}

export function fetchScimConnection(customerOrgId: string): Promise<ScimConnection | null> {
  return apiGet<ScimConnection | null>(`/admin/customers/${customerOrgId}/scim`);
}

export function configureScim(
  customerOrgId: string,
  input: ConfigureScimInput,
): Promise<ScimConnection> {
  return apiPost<ScimConnection>(`/admin/customers/${customerOrgId}/scim`, input);
}

export interface ScimEventInput {
  scimConnectionId: string;
  eventType: 'user.add' | 'user.remove' | 'group.add' | 'group.remove';
  externalUserId?: string;
  externalGroupId?: string;
  email?: string;
}

export function emitScimEvent(input: ScimEventInput): Promise<{ processed: boolean }> {
  return apiPost<{ processed: boolean }>('/admin/scim/events', input);
}

// ---- Sales / account assignments --------------------------------------------

export type AssignmentType =
  | 'account_owner'
  | 'ae'
  | 'sdr'
  | 'support_rep'
  | 'reseller_manager'
  | 'customer_success';

export interface SalesAccount {
  customerOrgId: string;
  customerDisplayName: string;
  orgType: OrgType;
  status: OrgStatus;
  assignments: AccountAssignment[];
}

export interface AccountAssignment {
  id: string;
  customer_org_id: string;
  assigned_user_zitadel_id: string;
  assigned_user_email: string;
  assignment_type: AssignmentType;
  starts_at: string;
  ends_at: string | null;
}

export function fetchSalesAccounts(): Promise<SalesAccount[]> {
  return apiGet<SalesAccount[]>('/admin/sales/accounts');
}

export interface AssignRepInput {
  customerOrgId: string;
  assignedUserEmail: string;
  assignmentType: AssignmentType;
}

export function assignSalesRep(input: AssignRepInput): Promise<AccountAssignment> {
  return apiPost<AccountAssignment>('/admin/sales/accounts', input);
}

// ---- Audit explorer (GET /admin/audit, filter by category) ------------------

export type AuditCategory =
  | 'identity'
  | 'provisioning'
  | 'purchase'
  | 'entitlement'
  | 'scim'
  | 'microsoft_consent'
  | 'email_send'
  | 'authorization'
  | 'admin';

export interface AuditEvent {
  id: string;
  event_type: string;
  event_category: AuditCategory;
  actor_email: string | null;
  actor_org_id: string | null;
  target_org_id: string | null;
  target_user_zitadel_id: string | null;
  payload: Record<string, unknown>;
  occurred_at: string;
}

export interface AuditFilters {
  category?: AuditCategory;
  actorEmail?: string;
  targetOrgId?: string;
  limit?: number;
  offset?: number;
}

export function fetchAuditEvents(filters?: AuditFilters): Promise<AuditEvent[]> {
  return apiGet<AuditEvent[]>('/admin/audit', { params: filters });
}

// ---- Reseller permissions (used on customer detail) -------------------------

export function fetchResellerPermissions(
  resellerOrgId: string,
  customerOrgId: string,
): Promise<ResellerCustomerPermissions> {
  return apiGet<ResellerCustomerPermissions>(
    `/admin/resellers/${resellerOrgId}/customers/${customerOrgId}/permissions`,
  );
}

// ---- Analytics aggregate query ----------------------------------------------

export function runAggregateQuery(
  input: AggregateQueryRequest,
): Promise<AggregateQueryResponse> {
  return apiPost<AggregateQueryResponse>('/analytics/aggregate-query', input);
}
